/**
 * Vandaag: lijst, midden en rechts in één scherm (ontwerp 26).
 *
 * De gekozen zaak staat in de url (`?zaak=<id>`), zodat terug, verversen
 * en een gedeelde link op dezelfde zaak landen. Een pushmelding opent
 * `/?chat=<id>` of `/?mail=<id>`; die wordt hier naar de zaak vertaald.
 * Sessie 72: `&vak=mail|whatsapp|chat|telefoon` opent het antwoordvak
 * in die stand (Nieuw bericht, Mail of WhatsApp bij de klant), ook op de
 * iPhone meteen met de zaak open. Niet `?kanaal=`: dat is de kanaaltegel
 * van de lijst (sessie 70).
 *
 * iPhone: lijst en zaak onder elkaar. Een tik op een kaart opent de zaak
 * (klasse `zaak-open`), de terugpijl brengt je terug naar de lijst.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { v6Api, type Kanaal } from "./api";
import { V6Provider, useV6 } from "./context";
import { Lijst } from "./Lijst";
import { useLiveEvent, type LiveEvent } from "./live";
import { vakUitUrl } from "./nieuwBericht";
import { Midden } from "./Midden";
import { Rechts } from "./Rechts";
import { useBijtrekkenBijLive } from "./vangnet";

/** Werkplek live (28-10): de zaak achter `?chat=` eerst elke 1,5 s vragen, */
export const BRON_SNEL_MS = 1_500;
/** ... tot 10 s na de tik, */
export const BRON_SNEL_TOT_MS = 10_000;
/** ... daarna elke 3 s, */
export const BRON_TRAAG_MS = 3_000;
/** ... en na 90 s opgeven met een melding in woorden (motor sessie 6f: de nieuwe motor mag
 *  75 s over een beurt doen; nooit eerder opgeven dan de beurt zelf). */
export const BRON_OPGEVEN_MS = 90_000;

export const BRON_WACHT_TEKST = "Gesprek wordt geopend…";
export const BRON_NIET_GEVONDEN_TEKST =
  "Dit gesprek is nog niet gevonden; het staat in de lijst zodra het binnenkomt.";

type BronStand = "wacht" | "niet-gevonden";

export function WerkplekV6Vandaag() {
  const [params, setParams] = useSearchParams();
  // Werkplek live (28-10): na elke verbinding van de live-stroom en bij
  // terugkomst (slaapstand iPhone) zaak en lijst bijtrekken.
  useBijtrekkenBijLive();
  const zaakId = params.get("zaak");
  const startKanaal = vakUitUrl(params);
  const [mobielZaak, setMobielZaak] = useState(false);

  const kies = useCallback(
    (id: string | null, kanaal: Kanaal | null = null) => {
      setParams(
        (oud) => {
          const nieuw = new URLSearchParams(oud);
          nieuw.delete("chat");
          nieuw.delete("mail");
          if (id) nieuw.set("zaak", id);
          else nieuw.delete("zaak");
          if (id && kanaal) nieuw.set("vak", kanaal);
          else nieuw.delete("vak");
          return nieuw;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Een zaak die met een kanaal opent (Nieuw bericht, klantpagina) staat op
  // de iPhone meteen open, niet achter de lijst.
  const startSleutel = zaakId && startKanaal ? `${zaakId}:${startKanaal}` : null;
  const [gezienStart, setGezienStart] = useState<string | null>(null);
  if (startSleutel !== gezienStart) {
    setGezienStart(startSleutel);
    if (startSleutel) setMobielZaak(true);
  }

  // Pushmelding: /?chat=<sessie> of /?mail=<email> naar de zaak.
  // Werkplek live (28-10): de melding komt vaak al terwijl het bericht nog
  // geen zaak heeft. Dan niet één keer vragen en opgeven (leeg midden),
  // maar blijven vragen tot de zaak er is; een live-event met dezelfde
  // chatsessie en een zaak opent hem meteen. Lukt het niet binnen 90 s,
  // dan een melding in woorden en de bron uit de url, zodat de lijst weer
  // zelf kiest.
  const bronChat = params.get("chat");
  const bronMail = params.get("mail");
  const [nietGevonden, setNietGevonden] = useState(false);
  const bronUitEvent = useRef<((event: LiveEvent) => void) | null>(null);
  useLiveEvent((event) => bronUitEvent.current?.(event));
  // `kies` en `setParams` wisselen bij elke url-wijziging; de lus hieronder
  // hoort alleen bij de bron, anders begint de wachttijd steeds opnieuw.
  const kiesRef = useRef(kies);
  const setParamsRef = useRef(setParams);
  useEffect(() => {
    kiesRef.current = kies;
    setParamsRef.current = setParams;
  });
  useEffect(() => {
    const soort = bronChat ? "chat" : bronMail ? "mail" : null;
    const id = bronChat ?? bronMail;
    if (!soort || !id) return;
    let actief = true;
    let volgende: ReturnType<typeof setTimeout> | null = null;
    const begin = Date.now();
    const stop = () => {
      actief = false;
      bronUitEvent.current = null;
      if (volgende) clearTimeout(volgende);
      clearTimeout(opgeven);
    };
    const gevonden = (zaakId: string) => {
      if (!actief) return;
      stop();
      setNietGevonden(false);
      kiesRef.current(zaakId);
      setMobielZaak(true);
    };
    const vraag = () => {
      if (!actief) return;
      v6Api
        .zaakVanBron(soort, id)
        .then(({ zaak_id }) => {
          if (zaak_id) gevonden(zaak_id);
          else plan();
        })
        .catch((fout: unknown) => {
          // Recensie 28-10: een vaste weigering (404, 422, 403) komt niet
          // vanzelf goed; dan meteen de melding in plaats van 90 s wachten.
          const status = (fout as { response?: { status?: number } })?.response?.status;
          if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            geefOp();
          } else plan();
        });
    };
    const plan = () => {
      if (!actief) return;
      const wacht = Date.now() - begin < BRON_SNEL_TOT_MS ? BRON_SNEL_MS : BRON_TRAAG_MS;
      volgende = setTimeout(vraag, wacht);
    };
    const geefOp = () => {
      if (!actief) return;
      stop();
      setNietGevonden(true);
      // Alleen de bron uit de url; de lijst kiest daarna weer zelf.
      setParamsRef.current(
        (oud) => {
          const nieuw = new URLSearchParams(oud);
          nieuw.delete("chat");
          nieuw.delete("mail");
          return nieuw;
        },
        { replace: true },
      );
    };
    const opgeven = setTimeout(geefOp, BRON_OPGEVEN_MS);
    // Een WhatsApp-melding opent ook /?chat=<sessie>; het event draagt dan
    // kanaal "whatsapp". Het event is alleen het sein om meteen te vragen:
    // zaak-van-bron volgt ook een samengevoegde zaak (recensie 28-10).
    const kanalen = soort === "chat" ? ["chat", "whatsapp"] : [soort];
    bronUitEvent.current = (event) => {
      if (kanalen.includes(event.kanaal) && event.gesprek_id === id && event.zaak_id) {
        if (volgende) clearTimeout(volgende);
        volgende = null;
        vraag();
      }
    };
    vraag();
    return stop;
  }, [bronChat, bronMail]);

  // Een nieuwe bron of een gekozen zaak ruimt de melding "niet gevonden" op.
  const bronSleutel = bronChat || bronMail ? `${bronChat}:${bronMail}` : null;
  const [gezienBron, setGezienBron] = useState<string | null>(bronSleutel);
  if (bronSleutel !== gezienBron) {
    setGezienBron(bronSleutel);
    if (bronSleutel) setNietGevonden(false);
  }
  const [gezienZaak, setGezienZaak] = useState<string | null>(zaakId);
  if (zaakId !== gezienZaak) {
    setGezienZaak(zaakId);
    if (zaakId) setNietGevonden(false);
  }

  const bronWacht = Boolean(bronChat || bronMail);
  const bronStand: BronStand | null = bronWacht ? "wacht" : nietGevonden && !zaakId ? "niet-gevonden" : null;
  const onTerug = useCallback(() => {
    setNietGevonden(false);
    setMobielZaak(false);
    // Ook tijdens het wachten: de bron uit de url stopt de lus (recensie 28-10).
    setParamsRef.current(
      (oud) => {
        if (!oud.has("chat") && !oud.has("mail")) return oud;
        const nieuw = new URLSearchParams(oud);
        nieuw.delete("chat");
        nieuw.delete("mail");
        return nieuw;
      },
      { replace: true },
    );
  }, []);
  // Zolang de bron loopt of de melding staat, kiest de lijst niet zelf.
  const bronActief = bronStand !== null;

  return (
    <V6Provider zaakId={zaakId} kies={kies} startKanaal={startKanaal}>
      <VandaagVlakken
        mobielZaak={(mobielZaak && Boolean(zaakId)) || bronStand !== null}
        bronActief={bronActief}
        bronStand={bronStand}
        onOpen={() => setMobielZaak(true)}
        onTerug={onTerug}
      />
    </V6Provider>
  );
}

/**
 * Binnen de provider, zodat een keuze in de lijst via `useV6().kies` loopt:
 * die leegt het concept van de copiloot, een wachtende copilootvraag en het
 * vlak rechts (iPhone). Anders kon een vraag voor zaak A op zaak B landen.
 */
function VandaagVlakken({
  mobielZaak,
  bronActief,
  bronStand,
  onOpen,
  onTerug,
}: {
  mobielZaak: boolean;
  bronActief: boolean;
  bronStand: BronStand | null;
  onOpen: () => void;
  onTerug: () => void;
}) {
  const { kies } = useV6();
  const onKies = useCallback(
    (id: string) => {
      kies(id);
      onOpen();
    },
    [kies, onOpen],
  );
  const onEerste = useCallback(
    (id: string | null) => {
      if (bronActief) return;
      kies(id);
    },
    [kies, bronActief],
  );
  return (
    <div className={`v6-vandaag${mobielZaak ? " zaak-open" : ""}`}>
      <Lijst onKies={onKies} onEerste={onEerste} />
      {bronStand ? (
        <BronMidden stand={bronStand} onTerug={onTerug} />
      ) : (
        <Midden onTerug={onTerug} />
      )}
      <Rechts />
    </div>
  );
}

/**
 * Werkplek live (28-10): het midden terwijl een gesprek uit een pushmelding
 * nog geen zaak heeft. Zelfde rustige vorm als de lege staat van het midden.
 */
function BronMidden({ stand, onTerug }: { stand: BronStand; onTerug: () => void }) {
  return (
    <section className="v6-vlak v6-midden leeg" aria-label="Gesprek en antwoord">
      {stand === "wacht" ? (
        <div className="v6-leeg">
          <p role="status">{BRON_WACHT_TEKST}</p>
          {/* Recensie 28-10: op de iPhone is de lijst dan verborgen. */}
          <button type="button" className="v6-knop" onClick={onTerug}>
            Naar de lijst
          </button>
        </div>
      ) : (
        <div className="v6-leeg">
          <p role="status">{BRON_NIET_GEVONDEN_TEKST}</p>
          <button type="button" className="v6-knop" onClick={onTerug}>
            Naar de lijst
          </button>
        </div>
      )}
    </section>
  );
}
